/**
 * Admin: Normalize `target_brix` on existing recipes to the per-type
 * defaults (Feb 2026 one-shot cleanup).
 *
 * Same two-phase flow as the ingredient-Brix resync page:
 *   1) DRY-RUN preview — list every recipe whose target_brix would change,
 *      grouped by type. Read-only, safe to run repeatedly.
 *   2) APPLY — writes ONLY `target_brix` on each affected recipe. Every
 *      other field (ingredients, ingredient.brix, type, name…) is left
 *      untouched. Requires the exact confirmation phrase pulled from the
 *      server so the UI checkbox label and the server check can never
 *      drift.
 *
 * After apply the page keeps a summary banner AND automatically re-runs
 * preview, so the admin can immediately see the "zero remaining changes"
 * effect. There's also a link to /admin/brix-outliers for the follow-up
 * gut-check.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API } from '../App';

const EnvironmentBadge = ({ env }) => {
  if (!env) return null;
  const isPreview = env.is_preview;
  const cls = isPreview
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-green-100 text-green-800 border-green-300';
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wider ${cls}`}
      data-testid="norm-target-brix-env-badge"
      title={`MongoDB: ${env.db_name || '?'}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${isPreview ? 'bg-amber-500' : 'bg-green-500'}`} />
      {isPreview ? 'PREVIEW' : 'PRODUCTION'}
      <span className="opacity-60 font-mono normal-case tracking-normal">{env.db_name}</span>
    </span>
  );
};

const SummaryTile = ({ label, value, cls, testid }) => (
  <div className={`rounded-lg p-3 ${cls}`} data-testid={testid}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const fmt = (v) =>
  v === null || v === undefined ? (
    <span className="text-gray-400">null</span>
  ) : (
    <span className="tabular-nums">{Number(v).toFixed(1)}</span>
  );

const AdminNormalizeTargetBrixPage = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeTestData, setIncludeTestData] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Pull the server's canonical phrase ONCE so the checkbox label can't
  // drift out of sync with the server-side check.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/normalize-target-brix/confirm-phrase`);
        setConfirmPhrase(r.data?.phrase || '');
      } catch (_e) {
        // fall back to a hardcoded string is intentional NOT — apply will
        // just fail if the endpoint is unreachable, which is the safe default.
      }
    })();
  }, []);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/normalize-target-brix/preview`, {
        params: {
          include_unpublished: includeUnpublished,
          include_test_data: includeTestData,
        },
      });
      setPreview(r.data);
      setConfirmed(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke hente preview');
    } finally {
      setLoading(false);
    }
  }, [includeUnpublished, includeTestData]);

  // Auto-fetch on filter changes.
  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const filteredChanges = useMemo(() => {
    if (!preview) return [];
    return preview.changes.filter((c) => {
      if (typeFilter !== 'all' && (c.canonical_type || '').toLowerCase() !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(c.name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [preview, typeFilter, search]);

  const applyPlan = async () => {
    if (!preview || preview.summary.affected_recipe_count === 0) {
      toast.error('Ingen ændringer at anvende');
      return;
    }
    if (!confirmed) {
      toast.error('Bekræft først at du forstår hvad der sker');
      return;
    }
    setApplying(true);
    setApplyResult(null);
    try {
      const r = await axios.post(`${API}/admin/normalize-target-brix/apply`, {
        confirmation_phrase: confirmPhrase,
        include_unpublished: includeUnpublished,
        include_test_data: includeTestData,
      });
      setApplyResult(r.data);
      toast.success(`✓ ${r.data.summary.written} opskrifter opdateret`);
      // Auto-refresh preview so the admin can visually confirm zero remaining.
      await fetchPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke anvende ændringerne');
    } finally {
      setApplying(false);
    }
  };

  // Group changes by CANONICAL type (post-normalization) so aliased
  // variants like `milk`/`mælk` collapse under their common target key
  // (`maelk`). Shows the user the "target state" of the DB.
  const perTypeBuckets = useMemo(() => {
    if (!preview) return [];
    const buckets = {};
    for (const c of preview.changes) {
      const k = (c.canonical_type || '(tom)').toLowerCase();
      buckets[k] = (buckets[k] || 0) + 1;
    }
    return Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  }, [preview]);

  const canApply = preview && preview.summary.affected_recipe_count > 0 && confirmed && !applying;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="admin-normalize-target-brix-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Normalisér target_brix</h1>
          {preview && <EnvironmentBadge env={preview.environment} />}
        </div>
        <p className="text-gray-600">
          Engangs-oprydning: sætter <code>target_brix</code> på eksisterende opskrifter til
          type-defaultet (samme værdier som &quot;Opret opskrift&quot;-formularen). Kun{' '}
          <code>target_brix</code> ændres — ingredienser, ingredient.brix og alle andre
          felter er urørt.
        </p>
        {preview?.environment?.is_preview && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            ⚠️ Du kigger på <strong>preview-databasen</strong> (<code>{preview.environment.db_name}</code>).
            Test her først; derefter deploy og kør på production.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
        <span className="font-semibold text-blue-900">Datasæt:</span>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input
            type="checkbox"
            checked={includeTestData}
            onChange={(e) => setIncludeTestData(e.target.checked)}
            data-testid="norm-include-test"
          />
          Inkluder testdata
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input
            type="checkbox"
            checked={includeUnpublished}
            onChange={(e) => setIncludeUnpublished(e.target.checked)}
            data-testid="norm-include-unpublished"
          />
          Inkluder upublicerede
        </label>
        <button
          type="button"
          onClick={fetchPreview}
          disabled={loading}
          className="ml-auto px-3 py-1 border border-blue-300 rounded text-blue-800 hover:bg-blue-100 disabled:opacity-50"
          data-testid="norm-refetch-btn"
        >
          {loading ? 'Indlæser…' : 'Genberegn'}
        </button>
      </div>

      {/* Defaults display */}
      {preview?.defaults && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-xs font-semibold text-gray-700 mb-2">Type-defaults der anvendes:</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(preview.defaults).map(([type, brix]) => (
              <span key={type} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded">
                <span className="text-gray-500">{type}</span>
                <strong className="tabular-nums text-gray-900">{Number(brix).toFixed(1)}°Bx</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Summary tiles */}
      {preview && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <SummaryTile
            label="Vil blive ændret"
            value={preview.summary.affected_recipe_count}
            cls="bg-amber-50 text-amber-800"
            testid="norm-summary-affected"
          />
          <SummaryTile
            label="Uændrede"
            value={preview.summary.no_change_count}
            cls="bg-green-50 text-green-800"
            testid="norm-summary-nochange"
          />
          <SummaryTile
            label="Ukendt type (skipped)"
            value={preview.summary.skipped_unknown_type_count}
            cls="bg-gray-100 text-gray-800"
            testid="norm-summary-unknown"
          />
          <SummaryTile
            label="Skipped testdata"
            value={preview.summary.skipped_test_data}
            cls="bg-gray-100 text-gray-800"
            testid="norm-summary-testdata"
          />
          <SummaryTile
            label="Skipped upubliceret"
            value={preview.summary.skipped_unpublished}
            cls="bg-gray-100 text-gray-800"
            testid="norm-summary-unpublished"
          />
        </div>
      )}

      {/* Per-type distribution */}
      {perTypeBuckets.length > 0 && (
        <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg text-xs">
          <div className="font-semibold text-gray-700 mb-2">Fordeling per type:</div>
          <div className="flex flex-wrap gap-2">
            {perTypeBuckets.map(([t, n]) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${
                  typeFilter === t
                    ? 'bg-blue-600 text-white border-blue-700'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`norm-type-filter-${t}`}
              >
                <span>{t}</span>
                <span className="tabular-nums font-semibold">{n}</span>
              </button>
            ))}
            {typeFilter !== 'all' && (
              <button
                type="button"
                onClick={() => setTypeFilter('all')}
                className="text-xs text-gray-500 underline"
              >
                Nulstil filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      {preview && preview.changes.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <input
            type="text"
            placeholder="Søg opskriftsnavn…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm flex-1"
            data-testid="norm-search"
          />
          <span className="text-xs text-gray-500" data-testid="norm-count">
            {filteredChanges.length} af {preview.changes.length}
          </span>
        </div>
      )}

      {/* Changes table */}
      {preview && preview.changes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto mb-4 max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-semibold">Opskrift</th>
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold text-right">Gammel brix</th>
                <th className="px-3 py-2 font-semibold text-center">→</th>
                <th className="px-3 py-2 font-semibold text-right">Ny brix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredChanges.map((c) => (
                <tr
                  key={c.recipe_id}
                  data-testid={`norm-row-${c.recipe_id}`}
                  className="hover:bg-gray-50"
                >
                  <td className="px-3 py-2 font-medium text-gray-900">
                    <Link
                      to={`/recipes/${c.recipe_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {c.type_will_change ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-gray-500 line-through">{c.type}</span>
                        <span className="text-gray-400">→</span>
                        <span className="tabular-nums font-semibold text-emerald-700">{c.canonical_type}</span>
                      </span>
                    ) : (
                      <span className="text-gray-600">{c.canonical_type}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{c.brix_will_change ? fmt(c.old) : <span className="text-gray-400">—</span>}</td>
                  <td className="px-3 py-2 text-center text-gray-400">→</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                    {c.brix_will_change ? <>{fmt(c.new)}°Bx</> : <span className="text-gray-400">uændret</span>}
                  </td>
                </tr>
              ))}
              {filteredChanges.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                    Ingen rækker matcher filtrene.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {preview && preview.summary.affected_recipe_count === 0 && (
        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm mb-4">
          ✓ Ingen opskrifter har brug for normalisering — alt er allerede på type-defaultet.
        </div>
      )}

      {/* Unknown-type warnings */}
      {preview && preview.skipped_unknown_type && preview.skipped_unknown_type.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs">
          <div className="font-semibold text-amber-900 mb-1">
            {preview.skipped_unknown_type.length} opskrift(er) blev sprunget over pga. ukendt type
          </div>
          <div className="text-amber-800 mb-2">
            De har en <code>type</code>-værdi der ikke matcher listen ovenfor. Ret typen manuelt i &quot;Rediger opskrift&quot; og genkør derefter.
          </div>
          <ul className="space-y-0.5">
            {preview.skipped_unknown_type.slice(0, 10).map((s) => (
              <li key={s.recipe_id}>
                <Link
                  to={`/edit-recipe/${s.recipe_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-900 hover:underline"
                >
                  {s.name}
                </Link>
                <span className="ml-1 text-amber-700">
                  (type={JSON.stringify(s.type)})
                </span>
              </li>
            ))}
            {preview.skipped_unknown_type.length > 10 && (
              <li className="italic text-amber-700">
                …og {preview.skipped_unknown_type.length - 10} mere
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Apply gate */}
      {preview && preview.summary.affected_recipe_count > 0 && confirmPhrase && (
        <div className="p-4 border-2 border-amber-300 bg-amber-50 rounded-lg mb-4">
          <label className="flex items-start gap-3 cursor-pointer text-amber-900">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
              data-testid="norm-confirm-checkbox"
            />
            <span className="text-sm leading-relaxed">{confirmPhrase}</span>
          </label>
          <button
            type="button"
            onClick={applyPlan}
            disabled={!canApply}
            className="mt-3 px-5 py-2 bg-red-600 text-white rounded-md text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            data-testid="norm-apply-btn"
          >
            {applying
              ? 'Anvender…'
              : `Normalisér ${preview.summary.affected_recipe_count} opskrifter`}
          </button>
        </div>
      )}

      {/* Apply result */}
      {applyResult && (
        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 mb-4">
          <div className="font-semibold text-emerald-900 mb-2">
            ✓ Anvendt {new Date(applyResult.applied_at).toLocaleString('da-DK')}
          </div>
          <ul className="text-sm text-emerald-800 space-y-1">
            <li>Opskrifter opdateret: <strong>{applyResult.summary.written}</strong></li>
            {applyResult.summary.type_renamed > 0 && (
              <li>heraf type-navne normaliseret: <strong>{applyResult.summary.type_renamed}</strong> (fx <code>milk</code> → <code>maelk</code>)</li>
            )}
            {applyResult.summary.failed > 0 && (
              <li className="text-red-700">Fejlede: <strong>{applyResult.summary.failed}</strong></li>
            )}
          </ul>
          <div className="mt-3 flex gap-2 flex-wrap text-xs">
            <Link
              to="/admin/brix-outliers"
              className="px-3 py-1 bg-white border border-emerald-300 rounded hover:bg-emerald-100"
              data-testid="norm-goto-outliers-btn"
            >
              Åbn Brix-outliers →
            </Link>
            <Link
              to="/admin/resync-ingredient-brix"
              className="px-3 py-1 bg-white border border-emerald-300 rounded hover:bg-emerald-100"
            >
              Åbn Resync ingredient-Brix →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNormalizeTargetBrixPage;
